/**
 * Reusable table + summary shared between Personal Capital Strategy and
 * Business Capital Strategy pages. Both pages read from Airtable slugs
 * (personal-funding / business-funding respectively) and scope their
 * Period filter to Application Date.
 *
 * NOTE: this replaces the previous combined /funding-pipeline page — spec
 * §2 requires Personal and Business Capital Strategy to be two separate
 * sidebar entries so we have exactly six product pages.
 */
import React, { useMemo } from "react";
import { PageHeader, Card, StatusPill, Money, StatCard } from "../components/ui-bj/Primitives";
import { Loading, ErrorState } from "../components/ui-bj/States";
import { useAirtable } from "../hooks/useAirtable";
import { money, str, fmtDate, indexById, linkedName } from "../lib/airtableHelpers";
import ExportButtons from "../components/ui-bj/ExportButtons";
import PeriodScopeBar, { usePeriodFilter } from "../components/ui-bj/PeriodScopeBar";

const buildRows = (records, clientMap) =>
  records.map((r) => {
    const f = r.fields || {};
    return {
      id: r.id,
      client: linkedName(f["Client Link"], clientMap),
      lender: f["Lender Name"],
      applied: money(f["Amount Applied"]),
      approved: money(f["Amount Approved"]),
      status: f["Status"],
      date: f["Application Date"],
    };
  });

const CapitalStrategyPage = ({
  slug,
  productName,
  productSummary,
  testidPrefix,
  eyebrow,
  exportFilename,
  emptyLabel,
}) => {
  const records = useAirtable(slug);
  const clients = useAirtable("clients");
  const clientMap = useMemo(() => indexById(clients.records), [clients.records]);
  const { period, setPeriod, scope, setScope, filterByDate } = usePeriodFilter("all");

  if (records.loading || clients.loading) return <Loading label={`Loading ${productName}`} />;
  if (records.error) return <ErrorState message={records.error} onRetry={records.refetch} />;

  const allRows = buildRows(records.records, clientMap);
  // Period filter scopes to "Application Date".
  const rows = allRows.filter((r) => filterByDate(r.date));

  const totals = {
    applied: rows.reduce((s, r) => s + r.applied, 0),
    approved: rows.reduce((s, r) => s + r.approved, 0),
  };
  totals.pending = totals.applied - totals.approved;

  return (
    <div data-testid={`${testidPrefix}-page`}>
      <PageHeader
        eyebrow={eyebrow}
        title={productName}
        subtitle={`${rows.length} of ${allRows.length} engagements • ${productSummary} • ${scope === "period" ? "filtered by Application Date" : "all-time view"}.`}
        right={
          <div className="flex items-center gap-3 flex-wrap">
            <PeriodScopeBar scope={scope} setScope={setScope} period={period} setPeriod={setPeriod} testidPrefix={`${testidPrefix}-scope`} />
            <ExportButtons
              title={productName}
              filename={exportFilename}
              testidPrefix={`export-${testidPrefix}`}
              columns={[
                { key: "client", label: "Client" },
                { key: "lender", label: "Lender" },
                { key: "applied", label: "Amount Applied" },
                { key: "approved", label: "Amount Approved" },
                { key: "status", label: "Status" },
                { key: "date", label: "Application Date" },
              ]}
              rows={rows}
            />
          </div>
        }
      />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-6">
        <StatCard testid={`${testidPrefix}-total-applied`}  label="Total Applied"  value={totals.applied}  prefix="$" tone="white" />
        <StatCard testid={`${testidPrefix}-total-approved`} label="Total Approved" value={totals.approved} prefix="$" tone="green" />
        <StatCard testid={`${testidPrefix}-total-pending`}  label="Total Pending"  value={Math.max(totals.pending, 0)} prefix="$" tone="amber" />
      </div>

      <Card data-testid={`${testidPrefix}-table`} className="overflow-x-auto">
        <table className="w-full min-w-[800px]">
          <thead>
            <tr className="text-left">
              {["Client","Lender","Amount Applied","Amount Approved","Status","Application Date"].map((h) => (
                <th key={h} className="bj-table-header py-2 pr-3 whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={6} className="py-10 text-center text-[#5E4D40] text-sm">{emptyLabel}</td></tr>}
            {rows.map((r, i) => (
              <tr key={r.id} data-testid={`${testidPrefix}-row-${i}`} className="bj-table-row">
                <td className="py-3 pr-3 text-[#2A1C15] text-sm">{r.client}</td>
                <td className="py-3 pr-3 text-[#6E5022] text-sm">{str(r.lender)}</td>
                <td className="py-3 pr-3 text-sm"><Money value={r.applied} tone="white" /></td>
                <td className="py-3 pr-3 text-sm">{r.approved > 0 ? <Money value={r.approved} tone="green" /> : <span className="text-[#5E4D40]">—</span>}</td>
                <td className="py-3 pr-3">{r.status ? <StatusPill status={r.status} /> : <span className="text-[#5E4D40] text-sm">—</span>}</td>
                <td className="py-3 pr-3 text-[#5E4D40] text-sm bj-tnum">{fmtDate(r.date)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
};

export const PersonalCapitalStrategyPage = () => (
  <CapitalStrategyPage
    slug="personal-funding"
    productName="Personal Capital Strategy"
    productSummary="Personal capital consulting"
    testidPrefix="personal-capital"
    eyebrow="DASHBOARD / PERSONAL CAPITAL STRATEGY"
    exportFilename="personal-capital-strategy"
    emptyLabel="No personal engagements for this filter."
  />
);

export const BusinessCapitalStrategyPage = () => (
  <CapitalStrategyPage
    slug="business-funding"
    productName="Business Capital Strategy"
    productSummary="Business capital consulting"
    testidPrefix="business-capital"
    eyebrow="DASHBOARD / BUSINESS CAPITAL STRATEGY"
    exportFilename="business-capital-strategy"
    emptyLabel="No business engagements for this filter."
  />
);

export default CapitalStrategyPage;
